import React, { useState, useEffect, useCallback, useRef } from 'react';
import { customerService, settingsService, customFieldService, usersService } from '../services/api';
import Footer from '../components/Footer';
import Select from '../components/Select';
import SearchInput from '../components/SearchInput';
import ChangeHistory from '../components/ChangeHistory';
import { useInvoice } from '../context/InvoiceContext';

const API_BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'http://localhost:5000';

const formatINR = (n) => '\u20B9' + parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Customers = () => {
  const { currentUser, hasPermission, showNotification } = useInvoice();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const canDeleteCustomer = hasPermission('delete_customer');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', address: '', phone: '', email: '', dateOfBirth: '', levelInProgress: '', path: '', field: '', batch: '', batchLocation: '', diplomaOrDegree: '', twelfthMarks: '', customerType: '', customData: {} });
  const [showDroppedOut, setShowDroppedOut] = useState(false);
  const [sortDir, setSortDir] = useState('asc');
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterPath, setFilterPath] = useState('');
  const [filterField, setFilterField] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterBatch, setFilterBatch] = useState('');
  const [filterBatchLocation, setFilterBatchLocation] = useState('');
  const [visibleCols, setVisibleCols] = useState(null);
  const [showColPicker, setShowColPicker] = useState(false);
  const savePrefsTimer = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [docLabel, setDocLabel] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [fieldOptions, setFieldOptions] = useState(['Nursing', 'Other']);
  const [managingFields, setManagingFields] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [pathOptions, setPathOptions] = useState(['Ausbildung', 'Work visa', 'Bachelors', 'Masters']);
  const [managingPaths, setManagingPaths] = useState(false);
  const [newPathName, setNewPathName] = useState('');
  const [customFields, setCustomFields] = useState([]);
  const [managingCustomFields, setManagingCustomFields] = useState(false);
  const [newCustomFieldName, setNewCustomFieldName] = useState('');
  const [addingCustomField, setAddingCustomField] = useState(false);

  const loadCustomers = useCallback(async (droppedOut = showDroppedOut) => {
    try {
      const res = await customerService.list(droppedOut);
      setCustomers(res.data || []);
    } catch {}
  }, [showDroppedOut]);

  useEffect(() => {
    const init = async () => {
      // Auto-backfill customers from existing invoices/quotations on first load
      try { await customerService.backfill(); } catch {}
      loadCustomers(showDroppedOut);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDroppedOut]);

  useEffect(() => {
    settingsService.get('customer_fields').then(val => {
      if (val) {
        try { setFieldOptions(JSON.parse(val)); } catch {}
      }
    }).catch(() => {});
    settingsService.get('customer_paths').then(val => {
      if (val) {
        try { setPathOptions(JSON.parse(val)); } catch {}
      }
    }).catch(() => {});
    customFieldService.list().then(res => setCustomFields(res.data || [])).catch(() => {});
    usersService.getPreferences().then(res => {
      const prefs = res.preferences || {};
      if (prefs.customers_columns) setVisibleCols(prefs.customers_columns);
    }).catch(() => {});
  }, []);

  const savePrefsToApi = useCallback((cols) => {
    if (savePrefsTimer.current) clearTimeout(savePrefsTimer.current);
    savePrefsTimer.current = setTimeout(() => {
      usersService.savePreferences({ customers_columns: cols }).catch(() => {});
    }, 500);
  }, []);

  const loadDetail = useCallback(async (id) => {
    try {
      const res = await customerService.get(id);
      setCustomerDetail(res.data);
    } catch {}
    setHistoryLoading(true);
    customerService.history(id).then(res => setHistory(res.data || [])).catch(() => {}).finally(() => setHistoryLoading(false));
  }, []);

  const resetForm = useCallback(() => {
    setForm({ name: '', address: '', phone: '', email: '', levelInProgress: '', path: '', field: '', batch: '', batchLocation: '', diplomaOrDegree: '', twelfthMarks: '', customerType: '', dateOfBirth: '', customData: {} });
    setEditingId(null);
    setShowForm(false);
  }, []);

  useEffect(() => {
    if (!showForm) return;
    const handleKeyDown = (e) => { if (e.key === 'Escape') resetForm(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showForm, resetForm]);

  const openNewCustomer = () => {
    setForm({ name: '', address: '', phone: '', email: '', levelInProgress: '', path: '', field: '', batch: '', batchLocation: '', diplomaOrDegree: '', twelfthMarks: '', customerType: '', dateOfBirth: '', customData: {} });
    setEditingId(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (editingId) {
        await customerService.update(editingId, payload);
      } else {
        await customerService.create(payload);
      }
      await loadCustomers();
      resetForm();
      if (selectedCustomer && editingId === selectedCustomer) loadDetail(selectedCustomer);
    } catch (e) { showNotification(e.message || 'Failed to save customer', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this customer? Associated leads will be unlinked.')) return;
    try {
      await customerService.delete(id);
      await loadCustomers();
      if (selectedCustomer === id) setSelectedCustomer(null);
    } catch (e) { showNotification(e.message || 'Failed to delete customer', 'error'); }
  };

  const handleDropOut = async (id) => {
    const reason = window.prompt('Reason for dropping out this customer:');
    if (reason === null) return;
    if (!reason.trim()) { showNotification('A reason is required to drop out a customer', 'error'); return; }
    try {
      await customerService.dropOut(id, reason.trim());
      await loadCustomers();
      if (selectedCustomer === id) setSelectedCustomer(null);
    } catch (e) { showNotification(e.message || 'Failed to drop out customer', 'error'); }
  };

  const handleReinstate = async (id) => {
    try {
      await customerService.reinstate(id);
      await loadCustomers();
      if (selectedCustomer === id) setSelectedCustomer(null);
    } catch (e) { console.error('Failed to reinstate customer:', e); }
  };

  const startEdit = (c) => {
    setForm({ name: c.name, address: c.address || '', phone: c.phone || '', email: c.email || '', dateOfBirth: c.dateOfBirth || '', levelInProgress: c.levelInProgress || '', path: c.path || '', field: c.field || '', batch: c.batch || '', batchLocation: c.batchLocation || '', diplomaOrDegree: c.diplomaOrDegree || '', twelfthMarks: c.twelfthMarks || '', customerType: c.customerType || '', customData: c.customData || {} });
    setEditingId(c.id);
    setShowForm(true);
    setSelectedCustomer(null);
    setCustomerDetail(null);
  };

  const saveFieldOptions = async (opts) => {
    setFieldOptions(opts);
    try { await settingsService.set('customer_fields', JSON.stringify(opts)); } catch {}
  };

  const handleAddField = async () => {
    const name = newFieldName.trim();
    if (!name || fieldOptions.includes(name)) return;
    await saveFieldOptions([...fieldOptions, name]);
    setNewFieldName('');
  };

  const handleRemoveField = async (name) => {
    await saveFieldOptions(fieldOptions.filter(f => f !== name));
  };

  const savePathOptions = async (opts) => {
    setPathOptions(opts);
    try { await settingsService.set('customer_paths', JSON.stringify(opts)); } catch {}
  };

  const handleAddPath = async () => {
    const name = newPathName.trim();
    if (!name || pathOptions.includes(name)) return;
    await savePathOptions([...pathOptions, name]);
    setNewPathName('');
  };

  const handleRemovePath = async (name) => {
    await savePathOptions(pathOptions.filter(p => p !== name));
  };

  const handleAddCustomField = async () => {
    if (!newCustomFieldName.trim()) return;
    setAddingCustomField(true);
    try {
      const res = await customFieldService.create(newCustomFieldName.trim());
      setCustomFields(f => [...f, res.data]);
      setNewCustomFieldName('');
    } catch (e) { showNotification(e.message || 'Failed to add field', 'error'); }
    finally { setAddingCustomField(false); }
  };

  const handleDeleteCustomField = async (fieldId) => {
    try {
      await customFieldService.delete(fieldId);
      setCustomFields(f => f.filter(cf => cf.id !== fieldId));
    } catch (e) { showNotification(e.message || 'Failed to delete field', 'error'); }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedCustomer) return;
    setUploading(true);
    try {
      await customerService.uploadDocument(selectedCustomer, file, docLabel);
      setDocLabel('');
      e.target.value = '';
      await loadDetail(selectedCustomer);
    } catch (err) { console.error('Upload failed:', err); }
    finally { setUploading(false); }
  };

  const handleDocDelete = async (docId) => {
    if (!selectedCustomer) return;
    try {
      await customerService.deleteDocument(selectedCustomer, docId);
      await loadDetail(selectedCustomer);
    } catch (err) { console.error('Delete failed:', err); }
  };

  const docStatusColors = {
    due: '#92400E', partially_paid: '#1E40AF', paid: '#065F46', cancelled: '#991B1B',
    draft: '#475569', sent: '#1E40AF', accepted: '#065F46', rejected: '#991B1B', expired: '#6B7280',
    issued: '#065F46',
  };

  const ALL_COLUMNS = [
    { key: 'contact', label: 'Contact', default: true },
    { key: 'type', label: 'Type', default: true },
    { key: 'path', label: 'Path', default: true },
    { key: 'field', label: 'Field', default: true },
    { key: 'level', label: 'Level', default: true },
    { key: 'batch', label: 'Batch', default: true },
    { key: 'batchLocation', label: 'Batch Location', default: true },
    { key: 'diplomaOrDegree', label: "Diploma/Bachelor's Degree", default: false },
    { key: 'twelfthMarks', label: '12th Marks (%)', default: false },
    { key: 'createdAt', label: 'Created', default: true },
    { key: 'invoices', label: 'Invoices', default: true },
    ...customFields.map(f => ({ key: f.key, label: f.name, default: true, custom: true })),
  ];
  const cols = visibleCols || ALL_COLUMNS.filter(c => c.default).map(c => c.key);
  const isColVisible = (key) => cols.includes(key);
  const toggleCol = (key) => {
    const next = cols.includes(key) ? cols.filter(k => k !== key) : [...cols, key];
    setVisibleCols(next);
    savePrefsToApi(next);
  };

  const batchOptions = Array.from(new Set(customers.map(c => c.batch).filter(Boolean))).sort();
  const batchLocationOptions = Array.from(new Set(customers.map(c => c.batchLocation).filter(Boolean))).sort();

  const hasActiveCustomerFilters = filterType || filterPath || filterField || filterLevel || filterBatch || filterBatchLocation;
  const clearCustomerFilters = () => {
    setFilterType(''); setFilterPath(''); setFilterField(''); setFilterLevel(''); setFilterBatch(''); setFilterBatchLocation('');
  };

  const filteredCustomers = customers.filter(c => {
    if (filterType && c.customerType !== filterType) return false;
    if (filterPath && c.path !== filterPath) return false;
    if (filterField && c.field !== filterField) return false;
    if (filterLevel && c.levelInProgress !== filterLevel) return false;
    if (filterBatch && c.batch !== filterBatch) return false;
    if (filterBatchLocation && c.batchLocation !== filterBatchLocation) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const nameMatch = (c.name || '').toLowerCase().includes(q);
    const queryDigits = q.replace(/\D/g, '');
    const phoneDigits = (c.phone || '').replace(/\D/g, '');
    const phoneMatch = queryDigits.length > 0 && phoneDigits.includes(queryDigits);
    return nameMatch || phoneMatch;
  });

  return (
    <>
      <div className="main-content">
        <div className="container">
          <div className="flex-between mb-lg">
            <h1 style={{ display: 'inline-flex', alignItems: 'baseline', gap: '10px' }}>
              {showDroppedOut ? 'Dropped Out Customers' : 'Customers'}
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-secondary)', background: 'var(--color-gray-100)', padding: '2px 10px', borderRadius: '12px', lineHeight: '1.4', position: 'relative', top: '-2px' }}>{filteredCustomers.length}</span>
            </h1>
            <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
              {!selectedCustomer && (
                <div style={{ position: 'relative' }}>
                  <button onClick={() => setShowColPicker(v => !v)} className="btn btn-secondary">Columns</button>
                  {showColPicker && (
                    <>
                      <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={() => setShowColPicker(false)} />
                      <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '4px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 14px', zIndex: 1000, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', color: '#1a1a1a', width: 'max-content' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', marginBottom: '6px' }}>Show Columns</div>
                        {ALL_COLUMNS.map(c => (
                          <div key={c.key} style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: '#334155', flex: 1 }}>
                              <input type="checkbox" checked={isColVisible(c.key)} onChange={() => toggleCol(c.key)} style={{ accentColor: 'var(--color-primary)', margin: 0, width: '14px', height: '14px' }} />
                              {c.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              <button
                onClick={() => { setSelectedCustomer(null); setCustomerDetail(null); setShowDroppedOut(v => !v); }}
                className="btn btn-secondary"
              >
                {showDroppedOut ? '← Back to Customers' : 'Dropped Out Customers'}
              </button>
              {!showDroppedOut && <button onClick={openNewCustomer} className="btn btn-primary">+ Add Customer</button>}
            </div>
          </div>

          {/* Create/Edit Modal */}
          {showForm && (
            <div className="modal-overlay" onClick={resetForm}>
            <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="flex-between mb-lg">
                <h3>{editingId ? 'Edit Customer' : 'Add Customer'}</h3>
                <button onClick={resetForm} className="btn btn-secondary btn-sm">✕</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                <div className="form-group">
                  <label className="form-label">Name <span style={{ color: '#DC2626' }}>*</span></label>
                  <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    style={{ borderColor: !form.name.trim() ? '#DC2626' : undefined, boxShadow: !form.name.trim() ? '0 0 0 2px rgba(220,38,38,0.15)' : undefined }} placeholder="Customer name" />
                </div>
                <div className="form-group">
                  <label className="form-label">WhatsApp Phone No.</label>
                  <input className="form-input" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+49 ..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" type="email" value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@example.com" />
                </div>
                <div className="form-group">
                  <label className="form-label">Date of Birth</label>
                  <input className="form-input" type="date" value={form.dateOfBirth || ''} onChange={e => setForm(f => ({ ...f, dateOfBirth: e.target.value }))} />
                </div>
                <div className="form-group" style={{ position: 'relative' }}>
                  <label className="form-label">Path</label>
                  {isSuperAdmin && (
                    <button type="button" onClick={() => { setManagingPaths(v => !v); setNewPathName(''); }}
                      style={{ position: 'absolute', top: 0, right: 0, fontSize: '11px', color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                      {managingPaths ? 'Done' : 'Manage'}
                    </button>
                  )}
                  {managingPaths ? (
                    <div style={{ border: '1px solid var(--color-border)', borderRadius: '6px', padding: '10px', background: 'var(--color-background)' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: pathOptions.length ? '8px' : 0 }}>
                        {pathOptions.map(p => (
                          <span key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#EDE9FE', color: '#5B21B6', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 600 }}>
                            {p}
                            <button type="button" onClick={() => handleRemovePath(p)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5B21B6', padding: 0, lineHeight: 1, fontWeight: 700, fontSize: '13px', marginLeft: '2px' }}>×</button>
                          </span>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <input className="form-input" value={newPathName} onChange={e => setNewPathName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleAddPath()}
                          placeholder="New path name" style={{ flex: 1, margin: 0, fontSize: '13px', padding: '5px 8px' }} />
                        <button type="button" onClick={handleAddPath} className="btn btn-primary" style={{ padding: '5px 12px', fontSize: '13px' }}>Add</button>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '6px' }}>Paths added here also show up on Leads.</div>
                    </div>
                  ) : (
                    <Select
                      value={form.path}
                      onChange={val => setForm(f => ({ ...f, path: val }))}
                      options={[
                        { value: '', label: '— Select path —' },
                        ...pathOptions.map(p => ({ value: p, label: p })),
                      ]}
                    />
                  )}
                </div>
                <div className="form-group" style={{ position: 'relative' }}>
                  <label className="form-label">Working Field</label>
                  {isSuperAdmin && (
                    <button type="button" onClick={() => { setManagingFields(v => !v); setNewFieldName(''); }}
                      style={{ position: 'absolute', top: 0, right: 0, fontSize: '11px', color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                      {managingFields ? 'Done' : 'Manage'}
                    </button>
                  )}
                  {managingFields ? (
                    <div style={{ border: '1px solid var(--color-border)', borderRadius: '6px', padding: '10px', background: 'var(--color-background)' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: fieldOptions.length ? '8px' : 0 }}>
                        {fieldOptions.map(f => (
                          <span key={f} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#DBEAFE', color: '#1E40AF', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 600 }}>
                            {f}
                            <button type="button" onClick={() => handleRemoveField(f)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1E40AF', padding: 0, lineHeight: 1, fontWeight: 700, fontSize: '13px', marginLeft: '2px' }}>×</button>
                          </span>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <input className="form-input" value={newFieldName} onChange={e => setNewFieldName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleAddField()}
                          placeholder="New working field name" style={{ flex: 1, margin: 0, fontSize: '13px', padding: '5px 8px' }} />
                        <button type="button" onClick={handleAddField} className="btn btn-primary" style={{ padding: '5px 12px', fontSize: '13px' }}>Add</button>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '6px' }}>Working fields added here also show up on Leads.</div>
                    </div>
                  ) : (
                    <Select
                      value={form.field}
                      onChange={val => setForm(f => ({ ...f, field: val }))}
                      options={[
                        { value: '', label: '— Select working field —' },
                        ...fieldOptions.map(f => ({ value: f, label: f })),
                      ]}
                    />
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Customer Type</label>
                  <Select
                    value={form.customerType}
                    onChange={val => setForm(f => ({ ...f, customerType: val }))}
                    options={[
                      { value: '', label: '— Select type —' },
                      { value: 'paying', label: 'Paying Customer' },
                      { value: 'free_scheme', label: 'Free Scheme' },
                    ]}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Batch</label>
                  <input className="form-input" value={form.batch || ''} onChange={e => setForm(f => ({ ...f, batch: e.target.value }))} placeholder="e.g. Batch 03" />
                </div>
                <div className="form-group">
                  <label className="form-label">Batch Location</label>
                  <Select
                    value={form.batchLocation}
                    onChange={val => setForm(f => ({ ...f, batchLocation: val }))}
                    options={[
                      { value: '', label: '— Select location —' },
                      { value: 'Madurai', label: 'Madurai' },
                      { value: 'Coimbatore', label: 'Coimbatore' },
                    ]}
                  />
                </div>
                {form.path === 'Ausbildung' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Diploma/Bachelor's Degree</label>
                      <input className="form-input" value={form.diplomaOrDegree || ''} onChange={e => setForm(f => ({ ...f, diplomaOrDegree: e.target.value }))} placeholder="e.g. B.Sc Nursing" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">12th Marks (%)</label>
                      <input className="form-input" type="number" step="0.01" min="0" max="100" value={form.twelfthMarks || ''} onChange={e => setForm(f => ({ ...f, twelfthMarks: e.target.value }))} placeholder="e.g. 85.5" />
                    </div>
                  </>
                )}
              </div>
              <div className="form-group">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <label className="form-label" style={{ margin: 0 }}>Custom Fields</label>
                  {isSuperAdmin && (
                    <button type="button" onClick={() => { setManagingCustomFields(v => !v); setNewCustomFieldName(''); }}
                      style={{ fontSize: '11px', color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                      {managingCustomFields ? 'Done' : 'Manage'}
                    </button>
                  )}
                </div>
                {managingCustomFields ? (
                  <div style={{ border: '1px solid var(--color-border)', borderRadius: '6px', padding: '10px', background: 'var(--color-background)' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: customFields.length ? '8px' : 0 }}>
                      {customFields.map(f => (
                        <span key={f.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#DBEAFE', color: '#1E40AF', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 600 }}>
                          {f.name}
                          <button type="button" onClick={() => handleDeleteCustomField(f.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1E40AF', padding: 0, lineHeight: 1, fontWeight: 700, fontSize: '13px', marginLeft: '2px' }}>×</button>
                        </span>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input className="form-input" value={newCustomFieldName} onChange={e => setNewCustomFieldName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAddCustomField()}
                        placeholder="New field name" style={{ flex: 1, margin: 0, fontSize: '13px', padding: '5px 8px' }} />
                      <button type="button" onClick={handleAddCustomField} disabled={addingCustomField || !newCustomFieldName.trim()} className="btn btn-primary" style={{ padding: '5px 12px', fontSize: '13px' }}>
                        {addingCustomField ? '...' : 'Add'}
                      </button>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '6px' }}>Fields added here also show up on Leads.</div>
                  </div>
                ) : customFields.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                    {customFields.map(f => (
                      <div key={f.id} className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">{f.name}</label>
                        <input className="form-input" value={form.customData?.[f.key] || ''}
                          onChange={e => setForm(fm => ({ ...fm, customData: { ...fm.customData, [f.key]: e.target.value } }))} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>No custom fields yet.</div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Address</label>
                <textarea className="form-input" ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }} style={{ minHeight: '40px', resize: 'vertical', overflow: 'hidden' }} value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder={"Street, City\nState, PIN"} />
              </div>
              <div className="form-group">
                <label className="form-label">Language Level in Progress</label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {['', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map(lvl => (
                    <button key={lvl} type="button" onClick={() => setForm(f => ({ ...f, levelInProgress: lvl }))}
                      style={{ padding: '4px 12px', borderRadius: '2px', fontSize: '12px', fontWeight: 600, border: 'none', cursor: 'pointer',
                        background: form.levelInProgress === lvl ? '#92400E' : '#E2E8F0', color: form.levelInProgress === lvl ? '#fff' : '#475569',
                      }}>{lvl || 'None'}</button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-md)' }}>
                <button onClick={resetForm} className="btn btn-secondary">Cancel</button>
                <button onClick={handleSave} disabled={!form.name.trim() || saving} className="btn btn-primary">{saving ? 'Saving...' : 'Save'}</button>
              </div>
            </div>
            </div>
          )}

          {/* Customer Detail View */}
          {selectedCustomer && customerDetail && (
            <>
              <div style={{ marginBottom: 'var(--spacing-md)', display: 'flex', gap: 'var(--spacing-sm)' }}>
                <button onClick={() => { setSelectedCustomer(null); setCustomerDetail(null); setHistory([]); setShowHistory(false); }} className="btn btn-secondary btn-sm">Back to list</button>
                <button onClick={() => startEdit(customerDetail)} className="btn btn-secondary btn-sm">Edit</button>
              </div>
              <div className="card mb-md">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)' }}>{customerDetail.name}</div>
                    {customerDetail.address && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', whiteSpace: 'pre-line', marginTop: '4px' }}>{customerDetail.address}</div>}
                    <div style={{ display: 'flex', gap: 'var(--spacing-md)', marginTop: '4px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                      {customerDetail.phone && <span>WhatsApp: {customerDetail.phone}</span>}
                      {customerDetail.email && <span>Email: {customerDetail.email}</span>}
                      {customerDetail.dateOfBirth && <span>Age: {(() => { const today = new Date(); const b = new Date(customerDetail.dateOfBirth); let a = today.getFullYear() - b.getFullYear(); const m = today.getMonth() - b.getMonth(); if (m < 0 || (m === 0 && today.getDate() < b.getDate())) a--; return a; })()}</span>}
                      {customerDetail.path && <span>Path: {customerDetail.path}</span>}
                      {customFields.filter(f => customerDetail.customData?.[f.key]).map(f => (
                        <span key={f.key}>{f.name}: {customerDetail.customData[f.key]}</span>
                      ))}
                      {customerDetail.createdAt && <span>Created: {new Date(customerDetail.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                      {customerDetail.customerType === 'paying' && (
                        <span style={{ background: '#D1FAE5', color: '#065F46', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px', whiteSpace: 'nowrap' }}>Paying Customer</span>
                      )}
                      {customerDetail.customerType === 'free_scheme' && (
                        <span style={{ background: '#DBEAFE', color: '#1E40AF', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px', whiteSpace: 'nowrap' }}>Free Scheme</span>
                      )}
                      {customerDetail.levelInProgress && (
                        <span style={{ background: '#FEF3C7', color: '#92400E', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>Level: {customerDetail.levelInProgress}</span>
                      )}
                      {customerDetail.batch && (
                        <span style={{ background: '#FCE7F3', color: '#9D174D', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>Batch: {customerDetail.batch}</span>
                      )}
                      {customerDetail.batchLocation && (
                        <span style={{ background: '#E0E7FF', color: '#3730A3', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>{customerDetail.batchLocation}</span>
                      )}
                      {customerDetail.diplomaOrDegree && (
                        <span style={{ background: '#E0F2FE', color: '#075985', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>{customerDetail.diplomaOrDegree}</span>
                      )}
                      {customerDetail.twelfthMarks && (
                        <span style={{ background: '#ECFCCB', color: '#3F6212', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>12th: {customerDetail.twelfthMarks}%</span>
                      )}
                      {customerDetail.isDroppedOut && (
                        <span style={{ background: '#FEE2E2', color: '#991B1B', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>Dropped Out</span>
                      )}
                    </div>
                    {customerDetail.isDroppedOut && customerDetail.dropOutReason && (
                      <div style={{ fontSize: 'var(--font-size-sm)', color: '#991B1B', marginTop: '6px' }}>Reason: {customerDetail.dropOutReason}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Customer Documents */}
              <div className="card mb-md">
                <div className="card-header">
                  Documents <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>({(customerDetail.documents || []).length})</span>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: (customerDetail.documents || []).length > 0 ? '12px' : 0, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    className="form-input"
                    style={{ flex: '1 1 160px', minWidth: 0 }}
                    placeholder="Label (optional)"
                    value={docLabel}
                    onChange={e => setDocLabel(e.target.value)}
                  />
                  <label style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', background: 'var(--color-primary)', color: '#fff', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.7 : 1 }}>
                    {uploading ? 'Uploading…' : '+ Upload'}
                    <input type="file" accept=".pdf,.png,.jpg,.jpeg" style={{ display: 'none' }} onChange={handleDocUpload} disabled={uploading} />
                  </label>
                </div>
                {(customerDetail.documents || []).length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {(customerDetail.documents || []).map(doc => (
                      <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: 'var(--color-gray-50, #F9FAFB)', borderRadius: '6px', border: '1px solid var(--color-gray-100)' }}>
                        <span style={{ fontSize: '18px', flexShrink: 0 }}>{doc.mimeType === 'application/pdf' ? '📄' : '🖼️'}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <a href={`${API_BASE}/uploads/${doc.filename}`} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-primary)', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {doc.label || doc.originalName}
                          </a>
                          {doc.label && <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.originalName}</div>}
                          <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                            {(doc.fileSize / 1024).toFixed(0)} KB · {new Date(doc.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </div>
                        </div>
                        {isSuperAdmin && <button onClick={() => handleDocDelete(doc.id)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 8px', color: '#DC2626', flexShrink: 0 }}>Delete</button>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Linked Documents */}
              {[
                { title: 'Invoices', docs: customerDetail.invoices || [], numField: 'invoiceNumber', dateField: 'invoiceDate', amountField: 'grandTotal' },
              ].map(({ title, docs, numField, dateField, amountField }) => (
                <div key={title} className="card mb-md">
                  <div className="card-header">{title} <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>({docs.length})</span></div>
                  {docs.length === 0 ? (
                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', padding: 'var(--spacing-sm) 0' }}>No {title.toLowerCase()} yet</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                          <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Number</th>
                          <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Date</th>
                          <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Amount</th>
                          <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {docs.map(doc => (
                          <tr key={doc.id} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                            <td style={{ padding: 'var(--spacing-sm)', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)' }}>{doc[numField]}</td>
                            <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)' }}>{new Date(doc[dateField]).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                            <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', color: 'var(--color-primary)' }}>{formatINR(doc[amountField])}</td>
                            <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center' }}>
                              <span style={{ padding: '2px 8px', fontSize: '11px', fontWeight: 600, borderRadius: '2px', textTransform: 'uppercase', letterSpacing: '0.04em', color: docStatusColors[doc.status] || '#475569', background: `${docStatusColors[doc.status] || '#475569'}15` }}>
                                {(doc.status || '').replace('_', ' ')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}

              {/* Change History */}
              <div className="card mb-md">
                <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setShowHistory(!showHistory)}>
                  {showHistory ? '▾' : '▸'} History <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>({history.length})</span>
                </div>
                {showHistory && (
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <ChangeHistory history={history} loading={historyLoading} />
                  </div>
                )}
              </div>
            </>
          )}

          {/* Search & Filters */}
          {!selectedCustomer && customers.length > 0 && (
            <div style={{ display: 'flex', gap: '5px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
              <SearchInput
                placeholder="Search by name or phone..."
                value={search}
                onChange={setSearch}
                style={{ width: '240px', flexShrink: 0 }}
                inputStyle={{ fontSize: '13px', padding: '6px 10px' }}
              />
              <Select
                value={filterType}
                onChange={setFilterType}
                style={{ width: '160px', flexShrink: 0 }}
                buttonStyle={{ fontSize: '13px', padding: '6px 10px' }}
                options={[
                  { value: '', label: 'All Types' },
                  { value: 'paying', label: 'Paying Customer' },
                  { value: 'free_scheme', label: 'Free Scheme' },
                ]}
              />
              <Select
                value={filterPath}
                onChange={setFilterPath}
                style={{ width: '150px', flexShrink: 0 }}
                buttonStyle={{ fontSize: '13px', padding: '6px 10px' }}
                options={[
                  { value: '', label: 'All Paths' },
                  ...pathOptions.map(p => ({ value: p, label: p })),
                ]}
              />
              <Select
                value={filterField}
                onChange={setFilterField}
                style={{ width: '160px', flexShrink: 0 }}
                buttonStyle={{ fontSize: '13px', padding: '6px 10px' }}
                options={[
                  { value: '', label: 'All Working Fields' },
                  ...fieldOptions.map(f => ({ value: f, label: f })),
                ]}
              />
              <Select
                value={filterLevel}
                onChange={setFilterLevel}
                style={{ width: '120px', flexShrink: 0 }}
                buttonStyle={{ fontSize: '13px', padding: '6px 10px' }}
                options={[
                  { value: '', label: 'All Levels' },
                  ...['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map(lvl => ({ value: lvl, label: lvl })),
                ]}
              />
              <Select
                value={filterBatch}
                onChange={setFilterBatch}
                style={{ width: '130px', flexShrink: 0 }}
                buttonStyle={{ fontSize: '13px', padding: '6px 10px' }}
                options={[
                  { value: '', label: 'All Batches' },
                  ...batchOptions.map(b => ({ value: b, label: b })),
                ]}
              />
              <Select
                value={filterBatchLocation}
                onChange={setFilterBatchLocation}
                style={{ width: '160px', flexShrink: 0 }}
                buttonStyle={{ fontSize: '13px', padding: '6px 10px' }}
                options={[
                  { value: '', label: 'All Batch Locations' },
                  ...batchLocationOptions.map(l => ({ value: l, label: l })),
                ]}
              />
              {hasActiveCustomerFilters && (
                <button onClick={clearCustomerFilters} className="btn btn-secondary btn-sm" style={{ fontSize: '13px' }}>
                  Clear Filters
                </button>
              )}
            </div>
          )}

          {/* Customer List */}
          {!selectedCustomer && (
            customers.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">👥</div>
                <div className="empty-state-text">{showDroppedOut ? 'No dropped out customers' : 'No customers yet'}</div>
                {!showDroppedOut && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginTop: '4px' }}>Customers are created automatically when an invoice is marked as paid</div>}
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🔍</div>
                <div className="empty-state-text">No customers match your search</div>
              </div>
            ) : (
              <div className="card" style={{ overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                      <th
                        onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                        style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                        title="Sort by #"
                      >
                        # {sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : ''}
                      </th>
                      <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>Name</th>
                      {isColVisible('contact') && <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>Contact</th>}
                      {isColVisible('type') && <th style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>Type</th>}
                      {isColVisible('path') && <th style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>Path</th>}
                      {isColVisible('field') && <th style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>Field</th>}
                      {isColVisible('level') && <th style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>Level</th>}
                      {isColVisible('batch') && <th style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>Batch</th>}
                      {isColVisible('batchLocation') && <th style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>Batch Location</th>}
                      {isColVisible('diplomaOrDegree') && <th style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>Diploma/Bachelor's Degree</th>}
                      {isColVisible('twelfthMarks') && <th style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>12th Marks (%)</th>}
                      {customFields.filter(f => isColVisible(f.key)).map(f => (
                        <th key={f.id} style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>{f.name}</th>
                      ))}
                      {isColVisible('createdAt') && <th style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>Created</th>}
                      {isColVisible('invoices') && <th style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>Invoices</th>}
                      {showDroppedOut && <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Reason</th>}
                      <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(sortDir ? [...filteredCustomers].sort((a, b) => sortDir === 'asc' ? a.id - b.id : b.id - a.id) : filteredCustomers).map(c => (
                      <tr key={c.id} style={{ borderBottom: '1px solid var(--color-gray-100)', cursor: 'pointer' }} onClick={() => startEdit(c)}>
                        <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>#{c.id}</td>
                        <td title={c.name} style={{ padding: 'var(--spacing-sm)', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px' }}>{c.name}</td>
                        {isColVisible('contact') && <td title={c.phone || ''} style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }}>
                          {c.phone || '-'}
                        </td>}
                        {isColVisible('type') && <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap' }}>
                          {c.customerType === 'paying'
                            ? <span style={{ background: '#D1FAE5', color: '#065F46', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>Paying</span>
                            : c.customerType === 'free_scheme'
                            ? <span style={{ background: '#DBEAFE', color: '#1E40AF', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>Free Scheme</span>
                            : <span style={{ color: 'var(--color-text-secondary)' }}>-</span>}
                        </td>}
                        {isColVisible('path') && <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap' }}>
                          {c.path ? <span style={{ background: '#EDE9FE', color: '#5B21B6', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>{c.path}</span> : <span style={{ color: 'var(--color-text-secondary)' }}>-</span>}
                        </td>}
                        {isColVisible('field') && <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap' }}>
                          {c.field ? <span style={{ background: '#DBEAFE', color: '#1E40AF', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>{c.field}</span> : <span style={{ color: 'var(--color-text-secondary)' }}>-</span>}
                        </td>}
                        {isColVisible('level') && <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap' }}>
                          {c.levelInProgress ? <span style={{ background: '#FEF3C7', color: '#92400E', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>{c.levelInProgress}</span> : <span style={{ color: 'var(--color-text-secondary)' }}>-</span>}
                        </td>}
                        {isColVisible('batch') && <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap' }}>
                          {c.batch ? <span style={{ background: '#FCE7F3', color: '#9D174D', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>{c.batch}</span> : <span style={{ color: 'var(--color-text-secondary)' }}>-</span>}
                        </td>}
                        {isColVisible('batchLocation') && <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap' }}>
                          {c.batchLocation ? <span style={{ background: '#E0E7FF', color: '#3730A3', padding: '2px 8px', borderRadius: '2px', fontWeight: 600, fontSize: '11px' }}>{c.batchLocation}</span> : <span style={{ color: 'var(--color-text-secondary)' }}>-</span>}
                        </td>}
                        {isColVisible('diplomaOrDegree') && <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>
                          {c.diplomaOrDegree || '-'}
                        </td>}
                        {isColVisible('twelfthMarks') && <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>
                          {c.twelfthMarks ? `${c.twelfthMarks}%` : '-'}
                        </td>}
                        {customFields.filter(f => isColVisible(f.key)).map(f => (
                          <td key={f.id} style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>
                            {c.customData?.[f.key] || '-'}
                          </td>
                        ))}
                        {isColVisible('createdAt') && <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', whiteSpace: 'nowrap' }}>
                          {c.createdAt ? new Date(c.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                        </td>}
                        {isColVisible('invoices') && <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap' }}>
                          {c.invoiceCount > 0 ? <span style={{ background: '#DBEAFE', borderRadius: '2px', padding: '1px 7px', fontWeight: 600 }}>{c.invoiceCount}</span> : <span style={{ color: 'var(--color-text-secondary)' }}>-</span>}
                        </td>}
                        {showDroppedOut && <td title={c.dropOutReason || ''} style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>{c.dropOutReason || '-'}</td>}
                        <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                            {showDroppedOut ? (
                              <>
                                {canDeleteCustomer && <button onClick={() => handleReinstate(c.id)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px', color: '#065F46', whiteSpace: 'nowrap' }}>Reinstate</button>}
                                {canDeleteCustomer && <button onClick={() => handleDelete(c.id)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px', color: '#DC2626', whiteSpace: 'nowrap' }}>Delete</button>}
                              </>
                            ) : (
                              <>
                                <button onClick={() => { setSelectedCustomer(c.id); loadDetail(c.id); }} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px', whiteSpace: 'nowrap' }}>View</button>
                                <button onClick={() => startEdit(c)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px', whiteSpace: 'nowrap' }}>Edit</button>
                                {canDeleteCustomer && <button onClick={() => handleDropOut(c.id)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px', color: '#92400E', whiteSpace: 'nowrap' }}>Drop Out</button>}
                                {canDeleteCustomer && <button onClick={() => handleDelete(c.id)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px', color: '#DC2626', whiteSpace: 'nowrap' }}>Delete</button>}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Customers;
